import { Candidate, Job } from 'types/types';
import { AnalysisState, initialState } from './jobAnalysisTypes';

interface InitParams {
  candidateId?: string;
  jobId?: string;
  stepId?: string;
  selectedCandidate: Candidate | null;
  selectedJob: Job | null;
  user: { id?: string } | null;
  apiClient: {
    getCandidate: (id: string) => Promise<Candidate>;
    getJob: (id: string) => Promise<Job>;
  };
  activeStep: number;
  onSetCandidate: (c: Candidate) => void;
  onSetJob: (j: Job) => void;
  onSetActiveStep: (s: number) => void;
  onSetError: (e: string) => void;
  onSetAnalysisState: (s: AnalysisState) => void;
  onSetInitialized: () => void;
}

function buildAnalysisPath(candidateId: string, jobId: string, activeStep: number): string {
  let path = '/job-analysis';
  if (candidateId) {
    path += `/${candidateId}`;
    if (jobId) path += `/${jobId}${activeStep > 0 ? `/${activeStep}` : ''}`;
  }
  return path;
}

function preserveAnalysisState(
  prev: AnalysisState,
  candidate: Candidate | null,
  job: Job | null
): AnalysisState {
  const sameContext = prev.candidate === candidate && prev.job === job;
  return {
    ...initialState,
    candidate,
    job,
    analysis: sameContext ? prev.analysis : null,
    resume: sameContext ? prev.resume : null,
    savedResumeId: sameContext ? prev.savedResumeId : null,
  };
}

const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

function getMissingStep(step: number, analysisState: AnalysisState): string | null {
  switch (step) {
    case 0:
      return null;
    case 1:
      return analysisState.candidate ? null : 'candidate';
    case 2:
      if (!analysisState.candidate) return 'candidate';
      return analysisState.job ? null : 'job';
    case 3:
      if (!analysisState.candidate) return 'candidate';
      if (!analysisState.job) return 'job';
      return analysisState.analysis ? null : 'analysis';
    default:
      return null;
  }
}

async function initializeFromParams(params: InitParams): Promise<void> {
  const candidate = await resolveCandidate(params);
  const job = await resolveJob(params);
  const urlStep = params.stepId ? parseInt(params.stepId, 10) : candidate && job ? 2 : 0;
  if (!isNaN(urlStep) && urlStep !== params.activeStep) params.onSetActiveStep(urlStep);
  params.onSetAnalysisState({ ...initialState, candidate, job });
  params.onSetInitialized();
}

async function resolveCandidate(params: InitParams): Promise<Candidate | null> {
  const { candidateId, selectedCandidate, user, apiClient, onSetCandidate, onSetError } = params;
  if (candidateId && candidateId !== selectedCandidate?.id) {
    const fetched = await apiClient.getCandidate(candidateId);
    if (fetched) {
      onSetCandidate(fetched);
      return fetched;
    }
    onSetError('Candidate not found.');
    return selectedCandidate;
  }
  if (!selectedCandidate && user) {
    const fetched = await apiClient.getCandidate(user.id || '');
    if (fetched) {
      onSetCandidate(fetched);
      return fetched;
    }
  }
  return selectedCandidate;
}

async function resolveJob(params: InitParams): Promise<Job | null> {
  const { jobId, selectedJob, apiClient, onSetJob, onSetError } = params;
  if (jobId && jobId !== selectedJob?.id) {
    const fetched = await apiClient.getJob(jobId);
    if (fetched) {
      onSetJob(fetched);
      return fetched;
    }
    onSetError('Job not found.');
  }
  return selectedJob;
}

export {
  buildAnalysisPath,
  capitalize,
  getMissingStep,
  initializeFromParams,
  preserveAnalysisState,
};
